import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../services/api";
import { needsMigration, migrateLocalStorageToBackend } from "../utils/migrateLocalStorage";
import { track } from "../lib/posthog";

// Mapea el tipo de item del frontend al evento de PostHog correspondiente
const TRACK_EVENT_BY_TYPE = {
  ingresos: "ingreso_creado",
  fijos:    "gasto_creado",
  extras:   "gasto_creado",
};

// Detecta el origen: si vino de una ingesta (voz/foto) o fue manual
function detectSource(item) {
  if (item.ingestaId || item.ingesta_id) return "voz";
  return "manual";
}

// Mapper functions: Backend (snake_case) → Frontend (camelCase)
function mapFijo(f) {
  return {
    id: f.id,
    concepto: f.concepto,
    monto: f.monto_presupuesto,
    fechaLimite: f.fecha_limite_dia ? String(f.fecha_limite_dia) : "",
    pagado: f.pagado || false,
    montoPagado: f.monto_pagado ?? null,
    esRecurrente: f.es_recurrente || false,
    cuentaId: f.cuenta_id || null,
    recurrenteId: f.recurrente_id || null,
  };
}

function mapExtra(e) {
  return {
    id: e.id,
    concepto: e.concepto,
    monto: e.monto,
    cat: e.categoria,
    esRecurrente: e.es_recurrente || false,
    cuentaId: e.cuenta_id || null,
    fecha: e.fecha || null,
    // Sin recurrenteId, FinancialContext no puede saber que este extra ya
    // materializa a su recurrente y le inyecta además un ghost "rec_*":
    // el gasto se contaba DOS veces en totalExt, deudaTDC y el anual.
    recurrenteId: e.recurrente_id || null,
  };
}

function mapIngreso(i) {
  return {
    id: i.id,
    concepto: i.concepto,
    monto: i.monto,
    orden: i.orden || 0,
    cuentaId: i.cuenta_id || null,
    fecha: i.fecha || null,
    // Igual que en mapFijo/mapExtra: sin estos campos, FinancialContext no
    // puede saber que el ingreso ya materializa a su recurrente y le inyecta
    // además un ghost, contando el dinero dos veces.
    esRecurrente: i.es_recurrente || false,
    recurrenteId: i.recurrente_id || null,
  };
}

function mapAhorro(a) {
  return {
    metaId: a.meta_id,
    monto: a.monto,
    cuentaOrigenId: a.cuenta_origen_id || null,
    cuentaDestinoId: a.cuenta_destino_id || null,
  };
}

// Reverse mappers: Frontend (camelCase) → Backend (snake_case)
function unmapFijo(f) {
  const out = {
    concepto: f.concepto,
    monto_presupuesto: f.monto,
    fecha_limite_dia: f.fechaLimite ? Number(f.fechaLimite) : null,
    pagado: f.pagado || false,
    monto_pagado: f.montoPagado ?? null,
    es_recurrente: f.esRecurrente || false,
    cuenta_id: f.cuentaId || null,
  };
  if (f.recurrenteId) out.recurrente_id = f.recurrenteId;
  return out;
}

function unmapExtra(e) {
  return {
    concepto: e.concepto,
    monto: e.monto,
    categoria: e.cat,
    es_recurrente: e.esRecurrente || false,
    cuenta_id: e.cuentaId || null,
    fecha: e.fecha || null,
  };
}

function unmapIngreso(i) {
  return {
    concepto: i.concepto,
    monto: i.monto,
    orden: i.orden || 0,
    cuenta_id: i.cuentaId || null,
    fecha: i.fecha || null,
  };
}

function unmapAhorro(a) {
  return {
    meta_id: a.metaId,
    monto: a.monto,
    cuenta_origen_id: a.cuentaOrigenId || null,
    cuenta_destino_id: a.cuentaDestinoId || null,
  };
}

const emptyMonthData = () => ({
  ingresos: [],
  fijos: [],
  extras: [],
  ahorroMetas: [],
});

// Helper: check if an item is an empty placeholder (just created, no real data yet)
function isEmptyPlaceholder(item) {
  return !item.concepto && (item.monto === 0 || item.monto === undefined || item.monto === null);
}

// Helper: check if an item is a display-only ghost recurrente (injected by FinancialContext, not real backend data)
// Real backend items with esRecurrente=true have UUID ids, ghosts have "rec_*" ids
export function isRecurrenteGhost(item) {
  return item.esRecurrente === true && String(item.id).startsWith("rec_");
}

/**
 * Sync two arrays by diffing against the LAST SYNCED state.
 *
 * `synced` = what the backend currently has (from last successful sync or fetch)
 * `desired` = what the UI wants the backend to have
 *
 * This approach eliminates race conditions because we always compare against
 * the actual backend state, not a potentially stale "prev" from an overlapping call.
 */
async function syncArrays(synced, desired, createFn, updateFn, deleteFn, creatingIds) {
  const idUpdates = [];

  // Filter out recurrente ghost items — unless they've been paid (converted to real)
  const desiredReal = desired.filter(item => !isRecurrenteGhost(item) || item.pagado);
  const syncedReal = synced.filter(item => !isRecurrenteGhost(item));

  // CREATE: items in desired that are NOT in synced (by ID) and not empty
  for (const item of desiredReal) {
    if (isEmptyPlaceholder(item)) continue;

    const inSynced = syncedReal.find(s => s.id === item.id);
    if (!inSynced) {
      // This item doesn't exist on backend — CREATE it
      // Guard against duplicate in-flight creates
      if (creatingIds.has(item.id)) continue;
      creatingIds.add(item.id);
      try {
        const created = await createFn(item);
        if (created && created[0] && created[0].id) {
          idUpdates.push({ tempId: item.id, realId: created[0].id });
        }
      } catch (err) {
        console.error("Create failed:", err);
      } finally {
        creatingIds.delete(item.id);
      }
    } else {
      // Item exists on backend — check if it changed → UPDATE
      if (JSON.stringify(inSynced) !== JSON.stringify(item)) {
        try {
          await updateFn(item);
        } catch (err) {
          console.error("Update failed:", err);
        }
      }
    }
  }

  // DELETE: items in synced that are NOT in desired
  for (const item of syncedReal) {
    if (isEmptyPlaceholder(item)) continue;
    const stillWanted = desiredReal.find(d => d.id === item.id);
    if (!stillWanted) {
      try {
        await deleteFn(item);
      } catch (err) {
        console.error("Delete failed:", err);
      }
    }
  }

  return idUpdates;
}

export function useArboData(activeMonth) {
  const year = new Date().getFullYear();

  // Stale-while-revalidate: load cached data instantly, fetch fresh in background
  const cacheKey = `arbo_cache_${year}`;
  const cached = useState(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch { return null; }
  })[0];

  const [data, setData] = useState(() =>
    cached?.data || Object.fromEntries([...Array(12)].map((_, i) => [i, emptyMonthData()]))
  );
  const [mesIds, setMesIds] = useState(cached?.mesIds || {});
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);

  // Refs to always have fresh values in setMonth (avoids stale closures)
  const dataRef = useRef(data);
  dataRef.current = data;
  const mesIdsRef = useRef(mesIds);
  mesIdsRef.current = mesIds;

  // Track IDs currently being created to prevent duplicate POSTs
  const creatingIds = useRef(new Set());

  // IDs confirmed to exist in the backend (real IDs, not temp UUIDs)
  const knownBackendIds = useRef(new Set());

  // === KEY FIX: Track what the backend actually has (last synced state) ===
  // This is initialized from fetchYear and updated after each successful sync.
  // By comparing against this instead of a stale "prev", we eliminate race conditions.
  const lastSyncedRef = useRef(cached?.data ? (() => {
    const snap = {};
    for (let i = 0; i < 12; i++) {
      const md = cached.data[i] || emptyMonthData();
      snap[i] = {
        ingresos: md.ingresos.map(x => ({ ...x })),
        fijos: md.fijos.map(x => ({ ...x })),
        extras: md.extras.map(x => ({ ...x })),
        ahorroMetas: (md.ahorroMetas || []).map(x => ({ ...x })),
      };
    }
    return snap;
  })() : {});

  // === KEY FIX: Mutex to serialize sync operations ===
  // Only one sync runs at a time. If setMonth is called while syncing,
  // we just do the optimistic update and schedule a re-sync when done.
  const isSyncingRef = useRef(false);
  const syncAgainRef = useRef(false);
  const syncTimerRef = useRef(null);

  // WAL: bridge pending months from fetchYear → triggerSync (defined later)
  const pendingWalMonthsRef = useRef(new Set());

  // Fetch all year data in one call
  const fetchYear = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Migrar datos de localStorage si es la primera vez con backend
      if (needsMigration()) {
        await migrateLocalStorageToBackend(year);
      }

      const yearData = await api.meses.getYear(year);
      // yearData is { "0": { mes_id, ingresos, fijos, extras, ahorros }, ... }

      const newData = {};
      const newMesIds = {};

      for (let i = 0; i < 12; i++) {
        const monthRaw = yearData[String(i)] || {};
        newMesIds[i] = monthRaw.mes_id || null;
        newData[i] = {
          ingresos: (monthRaw.ingresos || []).map(mapIngreso),
          fijos: (monthRaw.fijos || []).map(mapFijo),
          extras: (monthRaw.extras || []).map(mapExtra),
          ahorroMetas: (monthRaw.ahorros || []).map(mapAhorro),
        };
      }

      // Initialize lastSyncedRef with backend data (deep clone)
      const syncedSnapshot = {};
      for (let i = 0; i < 12; i++) {
        syncedSnapshot[i] = {
          ingresos: newData[i].ingresos.map(x => ({ ...x })),
          fijos: newData[i].fijos.map(x => ({ ...x })),
          extras: newData[i].extras.map(x => ({ ...x })),
          ahorroMetas: newData[i].ahorroMetas.map(x => ({ ...x })),
        };
      }
      lastSyncedRef.current = syncedSnapshot;

      // WAL replay: merge any pending local items not yet in backend
      const monthsWithPending = new Set();
      for (let i = 0; i < 12; i++) {
        try {
          const walStr = localStorage.getItem(`arbo_wal_${i}`);
          if (!walStr) continue;
          const wal = JSON.parse(walStr);
          // Descartar WAL obsoleto (> 60 segundos). Una ventana de 60s es suficiente
          // para cubrir pérdida de conexión momentánea. Ventanas más largas aumentan
          // el riesgo de re-crear ítems que el usuario borró en la sesión anterior.
          if (!wal?.data || Date.now() - (wal.ts || 0) > 60 * 1000) {
            localStorage.removeItem(`arbo_wal_${i}`);
            continue;
          }
          const merge = (backend, walArr) => {
            const pending = (walArr || []).filter(w =>
              !isRecurrenteGhost(w) && !isEmptyPlaceholder(w) &&
              !backend.find(b => b.id === w.id)
            );
            return pending.length > 0 ? [...backend, ...pending] : backend;
          };
          const merged = {
            ...newData[i],
            ingresos: merge(newData[i].ingresos, wal.data.ingresos),
            fijos: merge(newData[i].fijos, wal.data.fijos),
            extras: merge(newData[i].extras, wal.data.extras),
          };
          const hasPending =
            merged.ingresos.length !== newData[i].ingresos.length ||
            merged.fijos.length    !== newData[i].fijos.length    ||
            merged.extras.length   !== newData[i].extras.length;
          if (hasPending) {
            newData[i] = merged;
            monthsWithPending.add(i);
          } else {
            // WAL has nothing new — safe to clear
            localStorage.removeItem(`arbo_wal_${i}`);
          }
        } catch {
          try { localStorage.removeItem(`arbo_wal_${i}`); } catch {}
        }
      }

      // Reset creating IDs and rebuild known backend IDs
      creatingIds.current = new Set();
      const ids = new Set();
      for (let i = 0; i < 12; i++) {
        newData[i].ingresos.forEach(x => ids.add(x.id));
        newData[i].fijos.forEach(x => ids.add(x.id));
        newData[i].extras.forEach(x => ids.add(x.id));
      }
      knownBackendIds.current = ids;

      setMesIds(newMesIds);
      setData(newData);
      // dataRef must also be updated before triggerSync reads it
      dataRef.current = newData;

      // Cache for stale-while-revalidate on next page load
      try { localStorage.setItem(cacheKey, JSON.stringify({ data: newData, mesIds: newMesIds })); } catch {}

      // Store months needing WAL sync — triggerSync runs after this resolves
      pendingWalMonthsRef.current = monthsWithPending;
    } catch (err) {
      setError(err.message);
      console.error("Failed to fetch year data:", err);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchYear();
  }, [fetchYear]);

  // Get current month shortcut
  const month = data[activeMonth] || emptyMonthData();
  const mesId = mesIds[activeMonth] || null;

  // === Core sync function — runs under mutex ===
  const doSync = useCallback(async (monthIdx) => {
    const currentMesId = mesIdsRef.current[monthIdx];
    if (!currentMesId) return;

    const synced = lastSyncedRef.current[monthIdx] || emptyMonthData();
    const desired = dataRef.current[monthIdx];

    if (!desired) return;

    const cIds = creatingIds.current;

    try {
      const ingresosUpdates = await syncArrays(
        synced.ingresos, desired.ingresos,
        (item) => api.ingresos.create({ mes_id: currentMesId, ...unmapIngreso(item) }),
        (item) => api.ingresos.update(item.id, unmapIngreso(item)),
        (item) => api.ingresos.remove(item.id),
        cIds,
      );

      const fijosUpdates = await syncArrays(
        synced.fijos, desired.fijos,
        (item) => api.gastos.fijos.create({ mes_id: currentMesId, ...unmapFijo(item) }),
        (item) => api.gastos.fijos.update(item.id, unmapFijo(item)),
        (item) => api.gastos.fijos.remove(item.id),
        cIds,
      );

      const extrasUpdates = await syncArrays(
        synced.extras, desired.extras,
        (item) => api.gastos.extras.create({ mes_id: currentMesId, ...unmapExtra(item) }),
        (item) => api.gastos.extras.update(item.id, unmapExtra(item)),
        (item) => api.gastos.extras.remove(item.id),
        cIds,
      );

      // Ahorros: upsert based on metaId
      const syncedAhorros = synced.ahorroMetas || [];
      for (const ahorro of (desired.ahorroMetas || [])) {
        const prevAhorro = syncedAhorros.find(a => a.metaId === ahorro.metaId);
        if (!prevAhorro || JSON.stringify(prevAhorro) !== JSON.stringify(ahorro)) {
          await api.metas.ahorros.upsert({
            meta_id: ahorro.metaId,
            mes_id: currentMesId,
            monto: ahorro.monto,
            cuenta_origen_id: ahorro.cuentaOrigenId || null,
            cuenta_destino_id: ahorro.cuentaDestinoId || null,
          });
        }
      }

      // Replace temp IDs with real backend IDs in local state
      const applyUpdates = (arr, updates) => {
        if (!updates || updates.length === 0) return arr;
        return arr.map(item => {
          const upd = updates.find(u => u.tempId === item.id);
          return upd ? { ...item, id: upd.realId } : item;
        });
      };

      const hasIdChanges = ingresosUpdates.length > 0 || fijosUpdates.length > 0 || extrasUpdates.length > 0;

      // Apply ID replacements synchronously to the ref FIRST,
      // then schedule React state update. This ensures lastSyncedRef
      // sees the real IDs immediately.
      if (hasIdChanges) {
        const cur = dataRef.current[monthIdx];
        const withRealIds = {
          ...cur,
          ingresos: applyUpdates(cur.ingresos, ingresosUpdates),
          fijos: applyUpdates(cur.fijos, fijosUpdates),
          extras: applyUpdates(cur.extras, extrasUpdates),
        };
        dataRef.current = { ...dataRef.current, [monthIdx]: withRealIds };

        // Also update React state
        setData(d => {
          const current = d[monthIdx];
          return {
            ...d,
            [monthIdx]: {
              ...current,
              ingresos: applyUpdates(current.ingresos, ingresosUpdates),
              fijos: applyUpdates(current.fijos, fijosUpdates),
              extras: applyUpdates(current.extras, extrasUpdates),
            },
          };
        });
      }

      // Update lastSyncedRef to reflect what the backend now has.
      // dataRef is already updated with real IDs at this point.
      const finalState = dataRef.current[monthIdx];
      lastSyncedRef.current[monthIdx] = {
        ingresos: finalState.ingresos
          .filter(x => !isRecurrenteGhost(x))
          .map(x => ({ ...x })),
        fijos: finalState.fijos
          .filter(x => !isRecurrenteGhost(x))
          .map(x => ({ ...x })),
        extras: finalState.extras
          .filter(x => !isRecurrenteGhost(x))
          .map(x => ({ ...x })),
        ahorroMetas: (finalState.ahorroMetas || []).map(x => ({ ...x })),
      };

      // WAL: clear after successful sync — data is now confirmed in backend
      try { localStorage.removeItem(`arbo_wal_${monthIdx}`); } catch {}

      // Actualizar cache con el estado post-sync (IDs reales ya aplicados)
      // para que en el próximo mount lastSyncedRef se inicialice correctamente.
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          data: dataRef.current,
          mesIds: mesIdsRef.current,
        }));
      } catch {}

    } catch (err) {
      setError(err.message);
      console.error("Failed to save month changes:", err);
      // On error, do NOT update lastSyncedRef — the backend state is uncertain.
      // The next sync will try again from the last known good state.
    }
  }, []);

  // === Trigger sync with debounce + mutex ===
  const triggerSync = useCallback((monthIdx) => {
    // Debounce: wait 300ms for rapid changes to settle
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);

    syncTimerRef.current = setTimeout(async () => {
      if (isSyncingRef.current) {
        // Already syncing — mark that we need to re-sync when done
        syncAgainRef.current = true;
        return;
      }

      isSyncingRef.current = true;
      try {
        await doSync(monthIdx);
      } finally {
        isSyncingRef.current = false;
        // If more changes came in while we were syncing, sync again
        if (syncAgainRef.current) {
          syncAgainRef.current = false;
          triggerSync(monthIdx);
        }
      }
    }, 300);
  }, [doSync]);

  // Direct save on blur — bypasses debounce, guarantees the item reaches the backend
  const saveItem = useCallback(async (type, item) => {
    const currentMesId = mesIdsRef.current[activeMonth];
    if (!currentMesId || isEmptyPlaceholder(item)) return;
    // Ghost recurrentes: skip unless they've been modified (e.g., marked as paid)
    if (isRecurrenteGhost(item) && !item.pagado) return;

    const createFns = {
      ingresos: (i) => api.ingresos.create({ mes_id: currentMesId, ...unmapIngreso(i) }),
      fijos:    (f) => api.gastos.fijos.create({ mes_id: currentMesId, ...unmapFijo(f) }),
      extras:   (e) => api.gastos.extras.create({ mes_id: currentMesId, ...unmapExtra(e) }),
    };
    const updateFns = {
      ingresos: (i) => api.ingresos.update(i.id, unmapIngreso(i)),
      fijos:    (f) => api.gastos.fijos.update(f.id, unmapFijo(f)),
      extras:   (e) => api.gastos.extras.update(e.id, unmapExtra(e)),
    };

    try {
      if (knownBackendIds.current.has(item.id)) {
        // Existing item → PATCH
        await updateFns[type](item);
        const synced = lastSyncedRef.current[activeMonth];
        if (synced?.[type]) {
          lastSyncedRef.current[activeMonth][type] = synced[type].map(x =>
            x.id === item.id ? { ...item } : x
          );
        }
      } else {
        // New item → POST
        if (creatingIds.current.has(item.id)) return;
        creatingIds.current.add(item.id);
        try {
          const created = await createFns[type](item);
          if (created?.[0]?.id) {
            const realId = created[0].id;
            knownBackendIds.current.add(realId);
            // Replace temp ID with real ID in state and refs
            setData(d => ({
              ...d,
              [activeMonth]: {
                ...d[activeMonth],
                [type]: d[activeMonth][type].map(x => x.id === item.id ? { ...x, id: realId } : x),
              },
            }));
            dataRef.current = {
              ...dataRef.current,
              [activeMonth]: {
                ...dataRef.current[activeMonth],
                [type]: dataRef.current[activeMonth][type].map(x =>
                  x.id === item.id ? { ...x, id: realId } : x
                ),
              },
            };
            // Update lastSyncedRef with the real-ID item
            const synced = lastSyncedRef.current[activeMonth] || emptyMonthData();
            lastSyncedRef.current[activeMonth] = {
              ...synced,
              [type]: [...(synced[type] || []), { ...item, id: realId }],
            };
            // Trackear creación exitosa (gasto_creado / ingreso_creado)
            try {
              const evt = TRACK_EVENT_BY_TYPE[type];
              if (evt) {
                const props = { source: detectSource(item) };
                if (type === "fijos") props.tipo = "fijo";
                if (type === "extras") props.tipo = "extra";
                track(evt, props);
              }
            } catch {}
          }
        } finally {
          creatingIds.current.delete(item.id);
        }
      }
      // Clear WAL for this month — data is confirmed in backend
      try { localStorage.removeItem(`arbo_wal_${activeMonth}`); } catch {}
    } catch (err) {
      console.error(`[Arbo] saveItem ${type} failed:`, err.message || err);
      setError(err.message || "Error al guardar");
    }
  }, [activeMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger WAL-pending syncs after fetch completes (pendingWalMonthsRef is set in fetchYear)
  useEffect(() => {
    const pending = pendingWalMonthsRef.current;
    if (pending.size === 0) return;
    pendingWalMonthsRef.current = new Set();
    const timer = setTimeout(() => {
      for (const i of pending) triggerSync(i);
    }, 500);
    return () => clearTimeout(timer);
  }); // intentionally no deps — runs after every render, self-guards with size===0

  // setMonth - update the active month and schedule auto-save
  const setMonth = useCallback(
    (updatesOrFn) => {
      const currentMesId = mesIdsRef.current[activeMonth];
      if (!currentMesId) return;

      const prev = dataRef.current[activeMonth];
      const next = typeof updatesOrFn === "function"
        ? updatesOrFn(prev)
        : { ...prev, ...updatesOrFn };

      // Optimistic update — UI responds immediately
      setData(d => ({ ...d, [activeMonth]: next }));
      dataRef.current = { ...dataRef.current, [activeMonth]: next };

      // WAL: persist pending data synchronously so browser refresh doesn't lose it
      try {
        localStorage.setItem(`arbo_wal_${activeMonth}`, JSON.stringify({
          data: next,
          ts: Date.now(),
        }));
      } catch {}

      // Actualizar la cache del año con el estado más reciente para evitar
      // que al reabrir la app se inicialice lastSyncedRef con datos obsoletos.
      // Esto previene el bug de "zombie data" donde ítems borrados reaparecen
      // porque la cache stale los incluía y el WAL los re-creaba al hacer merge.
      try {
        const updatedData = { ...dataRef.current, [activeMonth]: next };
        localStorage.setItem(cacheKey, JSON.stringify({
          data: updatedData,
          mesIds: mesIdsRef.current,
        }));
      } catch {}

      // Schedule backend sync (debounced + serialized)
      triggerSync(activeMonth);
    },
    [activeMonth, triggerSync]
  );

  const refetch = useCallback(() => {
    fetchYear();
  }, [fetchYear]);

  return {
    data,      // all 12 months: { 0: {...}, 1: {...}, ... }
    setData,   // direct setter (for recurrentes sync)
    month,     // shortcut to data[activeMonth]
    setMonth,  // update active month + auto-save
    saveItem,  // direct immediate save for a single item on blur
    mesId,     // active month's mes_id
    mesIds,    // all mes_ids
    loading,
    error,
    refetch,
  };
}
